import { io } from 'socket.io-client'
import Vue from 'vue'

export const NOTIFICATION_CREATED_EVENT = 'notification:created'
export const REPORT_EXPORT_UPDATED_EVENT = 'report-export:updated'

const REPORT_EXPORT_LINK_PREFIX = 'report-export:'
const REPORT_EXPORT_READY_MESSAGE = 'Relatório pronto para download'
const REPORT_EXPORT_FAILED_MESSAGE = 'Falha ao gerar o relatório'
const REPORT_EXPORT_EXPIRED_MESSAGE = 'Arquivo expirado. Gere o relatório novamente.'

const DOWNLOAD_FRAME_REMOVAL_DELAY_MS = 60000

let notificationSocket = null

export function getReportExportIdFromLink(link) {
    if (!link?.startsWith(REPORT_EXPORT_LINK_PREFIX)) return null

    return link.slice(REPORT_EXPORT_LINK_PREFIX.length) || null
}

function startFileDownload(downloadUrl) {
    const downloadFrame = document.createElement('iframe')

    downloadFrame.style.display = 'none'
    downloadFrame.src = downloadUrl
    document.body.appendChild(downloadFrame)
    setTimeout(() => downloadFrame.remove(), DOWNLOAD_FRAME_REMOVAL_DELAY_MS)
}

const getDefaultState = () => ({
    activeReportExports: [],
    notifications: [],
    unreadCount: 0,
})

const getters = {
    getActiveReportExports: (state) => state.activeReportExports,
    getNotifications: (state) => state.notifications,
    getUnreadCount: (state) => state.unreadCount,
}

const mutations = {
    setActiveReportExports(state, activeReportExports) {
        state.activeReportExports = activeReportExports
    },

    setAllNotificationsRead(state) {
        state.notifications = state.notifications.map((notification) => ({
            ...notification,
            notification_status: 1,
        }))
        state.unreadCount = 0
    },

    setNotificationList(state, notificationList) {
        state.notifications = notificationList.rows ?? []
        state.unreadCount = notificationList.unread_count ?? 0
    },
}

const actions = {
    connect(context) {
        if (notificationSocket) return

        const token = context.rootGetters['auth/getToken']

        if (!token) return

        const websocketConnection = Vue.prototype.$websocketConnection

        notificationSocket = io(websocketConnection.url, {
            auth: (sendAuth) => sendAuth({ token: context.rootGetters['auth/getToken'] }),
            path: websocketConnection.path,
        })
        notificationSocket.on('connect', () => {
            context.dispatch('fetch')
            context.dispatch('fetchActiveReportExports')
        })
        notificationSocket.on(NOTIFICATION_CREATED_EVENT, (event) =>
            context.dispatch('onNotificationCreated', event),
        )
        notificationSocket.on(REPORT_EXPORT_UPDATED_EVENT, (event) =>
            context.dispatch('onReportExportUpdated', event),
        )
    },

    disconnect() {
        notificationSocket?.disconnect()
        notificationSocket = null
    },

    async downloadReportExport(_context, reportExportId) {
        try {
            const response = await Vue.prototype.$http.post(
                `${Vue.prototype.$ipMessage}report-export/download/${reportExportId}`,
                {},
            )

            startFileDownload(response.data.download_url)
        } catch (error) {
            if (error?.response?.status === 404) {
                Vue.prototype.$toast.error(REPORT_EXPORT_EXPIRED_MESSAGE)

                return
            }
            Vue.prototype.$fnError(error)
        }
    },

    async fetch(context) {
        try {
            const response = await Vue.prototype.$http.post(
                `${Vue.prototype.$ipMessage}notification/v2/list`,
                {},
            )

            context.commit('setNotificationList', response.data)
        } catch (error) {
            console.error(error)
        }
    },

    async fetchActiveReportExports(context) {
        try {
            const response = await Vue.prototype.$http.post(
                `${Vue.prototype.$ipMessage}report-export/v2/active`,
                {},
            )

            context.commit('setActiveReportExports', response.data.rows ?? [])
        } catch (error) {
            console.error(error)
        }
    },

    async markAllRead(context) {
        try {
            await Vue.prototype.$http.post(
                `${Vue.prototype.$ipMessage}notification/v2/mark-all-read`,
                {},
            )
            context.commit('setAllNotificationsRead')
        } catch (error) {
            Vue.prototype.$fnError(error)
        }
    },

    async open(context, notification) {
        if (notification.notification_type === 'REPORT_EXPORT_DONE') {
            const reportExportId = getReportExportIdFromLink(notification.link)

            if (!reportExportId) return

            await context.dispatch('downloadReportExport', reportExportId)

            return
        }
        if (notification.notification_type === 'GENERIC' && notification.link) {
            window.open(notification.link, '_blank')
        }
    },

    onReportExportUpdated(context, event) {
        const isKnownExport = context.state.activeReportExports.some(
            (activeExport) => activeExport.id_report_export === event?.reportExportId,
        )

        if (!isKnownExport) return context.dispatch('fetchActiveReportExports')

        context.commit(
            'setActiveReportExports',
            context.state.activeReportExports.map((activeExport) =>
                activeExport.id_report_export === event.reportExportId
                    ? {
                          ...activeExport,
                          phase: event.phase,
                          state: 'active',
                          written_rows: event.writtenRows,
                      }
                    : activeExport,
            ),
        )
    },

    async onNotificationCreated(context, event) {
        await Promise.all([context.dispatch('fetch'), context.dispatch('fetchActiveReportExports')])

        if (!event?.reportExportId) return
        if (event.notificationType === 'REPORT_EXPORT_DONE') {
            Vue.prototype.$toast.success(REPORT_EXPORT_READY_MESSAGE)

            return
        }
        if (event.notificationType !== 'REPORT_EXPORT_FAILED') return

        const failedNotification = context.state.notifications.find(
            (notification) => getReportExportIdFromLink(notification.link) === event.reportExportId,
        )

        Vue.prototype.$toast.error(failedNotification?.description ?? REPORT_EXPORT_FAILED_MESSAGE)
    },
}

export default {
    actions,
    getters,
    mutations,
    state: getDefaultState,
}

export function syncNotificationSocket(store) {
    store.subscribe((mutation) => {
        if (mutation.type === 'auth/setToken' && mutation.payload)
            store.dispatch('notification/connect')
        if (mutation.type === 'auth/resetState') store.dispatch('notification/disconnect')
    })
}
